export const controlPlaneTypeDefs = `

scalar AWSDateTime

type Aggregation {
  _avg: Float!
  _max: Float!
  _min: Float!
  _p1: Float!
  _p5: Float!
  _p25: Float!
  _p50: Float!
  _p75: Float!
  _p95: Float!
  _p99: Float!
  _stdDev: Float!
  _sum: Float!
}

type AntennaDownlinkConfigData {
  bandwidth: Frequency!
  centerFrequency: Frequency!
  polarity: Polarization!
}

input AntennaDownlinkConfigDataFilterInput {
  _and: [AntennaDownlinkConfigDataFilterInput!]
  _exists: Boolean
  _not: AntennaDownlinkConfigDataFilterInput
  _or: [AntennaDownlinkConfigDataFilterInput!]
  bandwidth: FrequencyFilterInput
  centerFrequency: FrequencyFilterInput
  polarity: PolarizationFilterInput
}

input AntennaDownlinkConfigDataSortInput {
  bandwidth: FrequencySortInput
  centerFrequency: FrequencySortInput
  polarity: SortOrder
}

type AntennaDownlinkDemodDecodeConfigData {
  bandwidth: Frequency!
  centerFrequency: Frequency!
  decodeConfig: DecodeConfigData
  demodulationConfig: DemodulationConfigData
  polarity: Polarization!
}

input AntennaDownlinkDemodDecodeConfigDataFilterInput {
  _and: [AntennaDownlinkDemodDecodeConfigDataFilterInput!]
  _exists: Boolean
  _not: AntennaDownlinkDemodDecodeConfigDataFilterInput
  _or: [AntennaDownlinkDemodDecodeConfigDataFilterInput!]
  bandwidth: FrequencyFilterInput
  centerFrequency: FrequencyFilterInput
  decodeConfig: DecodeConfigDataFilterInput
  demodulationConfig: DemodulationConfigDataFilterInput
  polarity: PolarizationFilterInput
}

input AntennaDownlinkDemodDecodeConfigDataSortInput {
  bandwidth: FrequencySortInput
  centerFrequency: FrequencySortInput
  decodeConfig: DecodeConfigDataSortInput
  demodulationConfig: DemodulationConfigDataSortInput
  polarity: SortOrder
}

type AntennaUplinkConfigData {
  centerFrequency: Frequency!
  polarity: Polarization!
  targetEirp: Measure!
  transmitDisabled: Boolean
}

input AntennaUplinkConfigDataFilterInput {
  _and: [AntennaUplinkConfigDataFilterInput!]
  _exists: Boolean
  _not: AntennaUplinkConfigDataFilterInput
  _or: [AntennaUplinkConfigDataFilterInput!]
  centerFrequency: FrequencyFilterInput
  polarity: PolarizationFilterInput
  targetEirp: MeasureFilterInput
  transmitDisabled: BooleanFilterInput
}

input AntennaUplinkConfigDataSortInput {
  centerFrequency: FrequencySortInput
  polarity: SortOrder
  targetEirp: MeasureSortInput
  transmitDisabled: SortOrder
}

type AutoTrack {
  criticality: Criticality
}

input AutoTrackFilterInput {
  _and: [AutoTrackFilterInput!]
  _exists: Boolean
  _not: AutoTrackFilterInput
  _or: [AutoTrackFilterInput!]
  criticality: CriticalityFilterInput
}

type AutoTrackSettings {
  autoTrack: AutoTrack
}

type AutoTrackSettingsData {
  """Criticality level for auto tracking"""
  criticality: Criticality
}

input AutoTrackSettingsDataFilterInput {
  _and: [AutoTrackSettingsDataFilterInput!]
  _exists: Boolean
  _not: AutoTrackSettingsDataFilterInput
  _or: [AutoTrackSettingsDataFilterInput!]
  criticality: CriticalityFilterInput
}

input AutoTrackSettingsDataSortInput {
  criticality: SortOrder
}

input AutoTrackSettingsFilterInput {
  _and: [AutoTrackSettingsFilterInput!]
  _exists: Boolean
  _not: AutoTrackSettingsFilterInput
  _or: [AutoTrackSettingsFilterInput!]
  autoTrack: AutoTrackFilterInput
}

input AutoTrackSettingsSortInput {
  autoTrack: AutoTrackSortInput
}

input AutoTrackSortInput {
  criticality: SortOrder
}

type AzElProgramTrackSettings {
  ephemerisId: String!
}

type AzElProgramTrackSettingsData {
  """Ephemeris ID for Az-El tracking"""
  ephemerisId: String!
}

input AzElProgramTrackSettingsDataFilterInput {
  _and: [AzElProgramTrackSettingsDataFilterInput!]
  _exists: Boolean
  _not: AzElProgramTrackSettingsDataFilterInput
  _or: [AzElProgramTrackSettingsDataFilterInput!]
  ephemerisId: StringFilterInput
}

input AzElProgramTrackSettingsDataSortInput {
  ephemerisId: SortOrder
}

input AzElProgramTrackSettingsFilterInput {
  _and: [AzElProgramTrackSettingsFilterInput!]
  _exists: Boolean
  _not: AzElProgramTrackSettingsFilterInput
  _or: [AzElProgramTrackSettingsFilterInput!]
  ephemerisId: StringFilterInput
}

input AzElProgramTrackSettingsSortInput {
  ephemerisId: SortOrder
}

input BooleanFilterInput {
  _eq: Boolean
  _exists: Boolean
  _neq: Boolean
}

type BucketData {
  arn: String!
  region: String
}

input BucketDataFilterInput {
  _and: [BucketDataFilterInput!]
  _exists: Boolean
  _not: BucketDataFilterInput
  _or: [BucketDataFilterInput!]
  arn: StringFilterInput
  region: StringFilterInput
}

input BucketDataSortInput {
  arn: SortOrder
  region: SortOrder
}

type CapabilityDataflowTypeRule {
  """Dataflow type for this rule"""
  dataflowType: DataflowType!

  """CapabilityRule type for this rule"""
  type: CapabilityRuleType!
}

input CapabilityDataflowTypeRuleFilterInput {
  _and: [CapabilityDataflowTypeRuleFilterInput!]
  _exists: Boolean
  _not: CapabilityDataflowTypeRuleFilterInput
  _or: [CapabilityDataflowTypeRuleFilterInput!]
  dataflowType: DataflowTypeFilterInput
  type: CapabilityRuleTypeFilterInput
}

type CapabilityRule {
  """Dataflow type rule"""
  dataflow: CapabilityDataflowTypeRule
}

input CapabilityRuleFilterInput {
  _and: [CapabilityRuleFilterInput!]
  _exists: Boolean
  _not: CapabilityRuleFilterInput
  _or: [CapabilityRuleFilterInput!]
  dataflow: CapabilityDataflowTypeRuleFilterInput
}

enum CapabilityRuleType {
  DATA_FLOW
}

input CapabilityRuleTypeFilterInput {
  _eq: CapabilityRuleType
  _exists: Boolean
  _in: [CapabilityRuleType!]
  _neq: CapabilityRuleType
  _nin: [CapabilityRuleType!]
}

type Config {
  accountId: String!
  data: ConfigData!
  expirationTime: AWSDateTime
  id: String!
  internalCreatorAccountId: String
  name: String
  region: String!
  timestamp: AWSDateTime
  type: ConfigType!
  version: Int
}

type ConfigAggregation {
  version: Aggregation!
}

enum ConfigCapabilityType {
  ANTENNA_DOWNLINK
  ANTENNA_DOWNLINK_DEMOD_DECODE
  ANTENNA_UPLINK
  CONNECTION_PROXY
  DATAFLOW_ENDPOINT
  MANAGED_ANTENNA_DOWNLINK_DEMOD_DECODE
  S3_PLAYBACK
  S3_RECORDING
  TELEMETRY_SINK
  TRACKING
  UPLINK_ECHO
}

input ConfigCapabilityTypeFilterInput {
  _eq: ConfigCapabilityType
  _exists: Boolean
  _in: [ConfigCapabilityType!]
  _neq: ConfigCapabilityType
  _nin: [ConfigCapabilityType!]
}

type ConfigData {
  antennaDownlink: AntennaDownlinkConfigData
  antennaDownlinkDemodDecode: AntennaDownlinkDemodDecodeConfigData
  antennaUplink: AntennaUplinkConfigData
  dataflowEndpoint: DataflowEndpointConfigData
  managedAntennaDownlinkDemodDecode: ManagedAntennaDownlinkDemodDecodeConfigData
  s3Playback: S3PlaybackConfigData
  s3Recording: S3RecordingConfigData
  telemetrySink: TelemetrySinkData
  tracking: TrackingConfigData
  uplinkEcho: UplinkEchoConfigData
}

input ConfigDataFilterInput {
  _and: [ConfigDataFilterInput!]
  _exists: Boolean
  _not: ConfigDataFilterInput
  _or: [ConfigDataFilterInput!]
  antennaDownlink: AntennaDownlinkConfigDataFilterInput
  antennaDownlinkDemodDecode: AntennaDownlinkDemodDecodeConfigDataFilterInput
  antennaUplink: AntennaUplinkConfigDataFilterInput
  dataflowEndpoint: DataflowEndpointConfigDataFilterInput
  managedAntennaDownlinkDemodDecode: ManagedAntennaDownlinkDemodDecodeConfigDataFilterInput
  s3Playback: S3PlaybackConfigDataFilterInput
  s3Recording: S3RecordingConfigDataFilterInput
  telemetrySink: TelemetrySinkDataFilterInput
  tracking: TrackingConfigDataFilterInput
  uplinkEcho: UplinkEchoConfigDataFilterInput
}

input ConfigDataSortInput {
  antennaDownlink: AntennaDownlinkConfigDataSortInput
  antennaDownlinkDemodDecode: AntennaDownlinkDemodDecodeConfigDataSortInput
  antennaUplink: AntennaUplinkConfigDataSortInput
  dataflowEndpoint: DataflowEndpointConfigDataSortInput
  managedAntennaDownlinkDemodDecode: ManagedAntennaDownlinkDemodDecodeConfigDataSortInput
  s3Playback: S3PlaybackConfigDataSortInput
  s3Recording: S3RecordingConfigDataSortInput
  telemetrySink: TelemetrySinkDataSortInput
  tracking: TrackingConfigDataSortInput
  uplinkEcho: UplinkEchoConfigDataSortInput
}

input ConfigFilterInput {
  _and: [ConfigFilterInput!]
  _exists: Boolean
  _not: ConfigFilterInput
  _or: [ConfigFilterInput!]
  accountId: StringFilterInput
  data: ConfigDataFilterInput
  expirationTime: TimestampFilterInput
  id: StringFilterInput
  internalCreatorAccountId: StringFilterInput
  name: StringFilterInput
  region: StringFilterInput
  timestamp: TimestampFilterInput
  type: ConfigTypeFilterInput
  version: IntegerFilterInput
}

type ConfigIdToDagNodeMapEntry {
  key: String
  value: DagNode
}

input ConfigIdToDagNodeMapFilterInput {
  _and: [ConfigIdToDagNodeMapFilterInput!]
  _exists: Boolean
  _not: ConfigIdToDagNodeMapFilterInput
  _or: [ConfigIdToDagNodeMapFilterInput!]
  key: StringFilterInput
  value: DagNodeFilterInput
}

input ConfigSortInput {
  accountId: SortOrder
  data: ConfigDataSortInput
  expirationTime: SortOrder
  id: SortOrder
  internalCreatorAccountId: SortOrder
  name: SortOrder
  region: SortOrder
  timestamp: SortOrder
  type: SortOrder
  version: SortOrder
}

enum ConfigType {
  ANTENNA_DOWNLINK
  ANTENNA_DOWNLINK_DEMOD_DECODE
  ANTENNA_UPLINK
  DATAFLOW_ENDPOINT
  MANAGED_ANTENNA_DOWNLINK_DEMOD_DECODE
  S3_PLAYBACK
  S3_RECORDING
  TELEMETRY_SINK
  TRACKING
  UPLINK_ECHO
}

input ConfigTypeFilterInput {
  _eq: ConfigType
  _exists: Boolean
  _in: [ConfigType!]
  _neq: ConfigType
  _nin: [ConfigType!]
}

type Contact {
  _atTime(timestamp: AWSDateTime!): Contact
  _history: [Contact]!

  """AWS Account ID"""
  accountId: String!

  """Active contact version ID"""
  activeContactVersionId: Int

  """Antenna System ID"""
  antennaSystemId: String

  """Contact ID (same as id for compatibility)"""
  contactId: String!

  """Contact status"""
  contactStatus: ContactStatus!

  """Contact type"""
  contactType: ContactType!

  """TTL expiration time for automatic item deletion"""
  expirationTime: AWSDateTime

  """Ground Station ID"""
  groundStationId: String!

  """Contact ID - unique identifier for the contact"""
  id: String!

  """
  Lifecycle timestamps including creation, update, and cancellation times
  """
  lifecycleInfo: LifecycleInfo

  """Metering and billing information"""
  meteringInfo: MeteringInfo

  """Migration version for schema versioning"""
  migrationVersion: Int

  """Mission Profile"""
  missionProfile: MissionProfile

  """Mission Profile ARN"""
  missionProfileArn: String!

  """Mission Profile ID"""
  missionProfileId: String!

  """Regional information for contact creation and execution"""
  regionalInfo: RegionalInfo!

  """
  Reservation information including step function ID, reservation ARNs, and DAG nodes
  """
  reservationInfo: ReservationInfo

  """Satellite ARN"""
  satelliteArn: String

  """Satellite ID"""
  satelliteId: String

  """Signature maps"""
  sigMaps: SigMaps

  """List of telemetry route IDs"""
  telemetryRouteList: [String!]

  """Version timestamp indicating update time of this record"""
  timestamp: AWSDateTime!

  """Timing information including start, end, and visibility times"""
  timingInfo: TimingInfo!

  """
  Tracking information including elevation, pointing angles, and ephemeris data
  """
  trackingInfo: TrackingInfo

  """Version number indicating the number of updates made to this record"""
  version: Int!
}

type ContactAggregation {
  activeContactVersionId: Aggregation!
  meteringInfo: MeteringInfoAggregation!
  migrationVersion: Aggregation!
  missionProfile: MissionProfileAggregation!
  trackingInfo: TrackingInfoAggregation!
  version: Aggregation!
}

type ContactEphemerisInfo {
  """Ephemeris ID"""
  ephemerisId: String!

  """Ephemeris source type"""
  ephemerisSourceType: EphemerisSourceType

  """Update time for this ephemeris"""
  updateTime: AWSDateTime!

  """Usage type for this ephemeris"""
  usageType: ContactEphemerisUsageType!
}

input ContactEphemerisInfoFilterInput {
  _and: [ContactEphemerisInfoFilterInput!]
  _exists: Boolean
  _not: ContactEphemerisInfoFilterInput
  _or: [ContactEphemerisInfoFilterInput!]
  ephemerisId: StringFilterInput
  ephemerisSourceType: EphemerisSourceTypeFilterInput
  updateTime: TimestampFilterInput
  usageType: ContactEphemerisUsageTypeFilterInput
}

enum ContactEphemerisUsageType {
  DAILY_EPHEMERIS_REFRESH
  DAILY_EPHEMERIS_REFRESH_VALIDATE_VISIBILITY
  FINAL_POINTING_ANGLE_CALCULATION
  FINAL_POINTING_ANGLE_REFERENCE
  SCHEDULING
  UPDATE_CONTACT
}

input ContactEphemerisUsageTypeFilterInput {
  _eq: ContactEphemerisUsageType
  _exists: Boolean
  _in: [ContactEphemerisUsageType!]
  _neq: ContactEphemerisUsageType
  _nin: [ContactEphemerisUsageType!]
}

input ContactFilterInput {
  _and: [ContactFilterInput!]
  _exists: Boolean
  _not: ContactFilterInput
  _or: [ContactFilterInput!]
  accountId: StringFilterInput
  activeContactVersionId: IntegerFilterInput
  antennaSystemId: StringFilterInput
  contactId: StringFilterInput
  contactStatus: ContactStatusFilterInput
  contactType: ContactTypeFilterInput
  expirationTime: TimestampFilterInput
  groundStationId: StringFilterInput
  id: StringFilterInput
  lifecycleInfo: LifecycleInfoFilterInput
  meteringInfo: MeteringInfoFilterInput
  migrationVersion: IntegerFilterInput
  missionProfile: MissionProfileFilterInput
  missionProfileArn: StringFilterInput
  missionProfileId: StringFilterInput
  regionalInfo: RegionalInfoFilterInput
  reservationInfo: ReservationInfoFilterInput
  satelliteArn: StringFilterInput
  satelliteId: StringFilterInput
  sigMaps: SigMapsFilterInput
  telemetryRouteList: StringFilterInput
  timestamp: TimestampFilterInput
  timingInfo: TimingInfoFilterInput
  trackingInfo: TrackingInfoFilterInput
  version: IntegerFilterInput
}

input ContactSortInput {
  accountId: SortOrder
  activeContactVersionId: SortOrder
  antennaSystemId: SortOrder
  contactId: SortOrder
  contactStatus: SortOrder
  contactType: SortOrder
  expirationTime: SortOrder
  groundStationId: SortOrder
  id: SortOrder
  lifecycleInfo: LifecycleInfoSortInput
  meteringInfo: MeteringInfoSortInput
  migrationVersion: SortOrder
  missionProfile: MissionProfileSortInput
  missionProfileArn: SortOrder
  missionProfileId: SortOrder
  regionalInfo: RegionalInfoSortInput
  reservationInfo: ReservationInfoSortInput
  satelliteArn: SortOrder
  satelliteId: SortOrder
  sigMaps: SigMapsSortInput
  timestamp: SortOrder
  timingInfo: TimingInfoSortInput
  trackingInfo: TrackingInfoSortInput
  version: SortOrder
}

enum ContactStatus {
  AWS_CANCELLED
  AWS_FAILED
  CANCELLED
  CANCELLING
  COMPLETED
  FAILED
  FAILED_TO_SCHEDULE
  PASS
  POSTPASS
  PREPASS
  RESCHEDULING
  SCHEDULED
  SCHEDULING
}

input ContactStatusFilterInput {
  _eq: ContactStatus
  _exists: Boolean
  _in: [ContactStatus!]
  _neq: ContactStatus
  _nin: [ContactStatus!]
}

enum ContactTimeline {
  AS_SCHEDULED
  EXTENDED
  TRUNCATED
}

input ContactTimelineFilterInput {
  _eq: ContactTimeline
  _exists: Boolean
  _in: [ContactTimeline!]
  _neq: ContactTimeline
  _nin: [ContactTimeline!]
}

enum ContactType {
  ANTENNA_SPECIFIC
  GROUND_STATION_SPECIFIC
}

input ContactTypeFilterInput {
  _eq: ContactType
  _exists: Boolean
  _in: [ContactType!]
  _neq: ContactType
  _nin: [ContactType!]
}

type ContactVersion {
  """Timestamp when this version became active."""
  activatedTimestamp: AWSDateTime

  """AWS Account ID."""
  awsAccountId: String!
  contactId: String!
  contactVersion: Int!

  """Timestamp when this version was created."""
  createdTimestamp: AWSDateTime!

  """TTL expiration time in epoch seconds for automatic item deletion."""
  expirationTime: AWSDateTime!

  """Failure code if the version failed to update."""
  failureCode: String

  """Failure message if the version failed to update."""
  failureMessage: String

  """Ground Station ID."""
  groundStationId: String!
  id: String!

  """Satellite ARN."""
  satelliteArn: String

  """Status of the contact version."""
  status: ContactVersionStatus!

  """Timestamp when this version was superseded by a newer version."""
  supersededTimestamp: AWSDateTime

  """Version timestamp indicating update time of this record."""
  timestamp: AWSDateTime!

  """Tracking overrides for this contact version."""
  trackingOverrides: TrackingOverrides

  """ARN of the state machine that handles the update for this version."""
  updateContactStateMachineArn: String

  """Version number indicating the number of updates made to this record."""
  version: Int!
}

type ContactVersionAggregation {
  contactVersion: Aggregation!
  version: Aggregation!
}

input ContactVersionFilterInput {
  _and: [ContactVersionFilterInput!]
  _exists: Boolean
  _not: ContactVersionFilterInput
  _or: [ContactVersionFilterInput!]
  activatedTimestamp: TimestampFilterInput
  awsAccountId: StringFilterInput
  contactId: StringFilterInput
  contactVersion: IntegerFilterInput
  createdTimestamp: TimestampFilterInput
  expirationTime: TimestampFilterInput
  failureCode: StringFilterInput
  failureMessage: StringFilterInput
  groundStationId: StringFilterInput
  id: StringFilterInput
  satelliteArn: StringFilterInput
  status: ContactVersionStatusFilterInput
  supersededTimestamp: TimestampFilterInput
  timestamp: TimestampFilterInput
  trackingOverrides: TrackingOverridesFilterInput
  updateContactStateMachineArn: StringFilterInput
  version: IntegerFilterInput
}

input ContactVersionSortInput {
  activatedTimestamp: SortOrder
  awsAccountId: SortOrder
  contactId: SortOrder
  contactVersion: SortOrder
  createdTimestamp: SortOrder
  expirationTime: SortOrder
  failureCode: SortOrder
  failureMessage: SortOrder
  groundStationId: SortOrder
  id: SortOrder
  satelliteArn: SortOrder
  status: SortOrder
  supersededTimestamp: SortOrder
  timestamp: SortOrder
  trackingOverrides: TrackingOverridesSortInput
  updateContactStateMachineArn: SortOrder
  version: SortOrder
}

enum ContactVersionStatus {
  ACTIVE
  FAILED_TO_UPDATE
  SUPERSEDED
  UPDATING
}

input ContactVersionStatusFilterInput {
  _eq: ContactVersionStatus
  _exists: Boolean
  _in: [ContactVersionStatus!]
  _neq: ContactVersionStatus
  _nin: [ContactVersionStatus!]
}

enum Criticality {
  PREFERRED
  REMOVED
  REQUIRED
}

input CriticalityFilterInput {
  _eq: Criticality
  _exists: Boolean
  _in: [Criticality!]
  _neq: Criticality
  _nin: [Criticality!]
}

type CursorPart {
  key: String!
  value: String!
}

input CursorPartInput {
  key: String!
  value: String!
}

type DagNode {
  """Capability rules for this node"""
  capabilityRules: [CapabilityRule!]

  """Config ID"""
  configId: String!

  """DAG node semantic version"""
  dagNodeSemanticVersion: String

  """Dataflow endpoint details"""
  dataflowEndpointDetails: String

  """Dataflow source config ID"""
  dataflowSourceConfigId: String

  """UUID of this dataflow"""
  dataflowUuid: String

  """Execution context ID"""
  executionContextId: String

  """Input dataflow type"""
  inputDataflowType: DataflowType

  """KMS data encryption key"""
  kmsDataKey: String

  """ID of the KMS key"""
  kmsKeyId: String

  """List of matched capability ARNs"""
  matchedCapabilityArns: [String!]

  """Node config capability type"""
  nodeConfigCapabilityType: ConfigCapabilityType

  """Output dataflow type"""
  outputDataflowType: DataflowType

  """Map of output type to next config IDs"""
  outputTypeToNextConfigIdsMap: [StringToStringMapEntry!]

  """Map of output type to previous config IDs"""
  outputTypeToPreviousConfigIdsMap: [StringToStringMapEntry!]

  """Region where config needs to be reserved"""
  region: String!

  """Reservation ID"""
  reservationId: String

  """Reservation status"""
  reservationStatus: ReservationStatus

  """Reserved capability ARN"""
  reservedCapabilityArn: String

  """S3 recording details"""
  s3RecordingDetails: String

  """Set of config IDs in same reservation group"""
  sameReservationGroupAsConfigIds: [String!]

  """Selected laminar flow version"""
  selectedLaminarFlowVersion: String

  """Supported laminar flow versions"""
  supportedLaminarFlowVersions: [String!]

  """Vita49 stream ID"""
  vita49StreamId: Int
}

input DagNodeFilterInput {
  _and: [DagNodeFilterInput!]
  _exists: Boolean
  _not: DagNodeFilterInput
  _or: [DagNodeFilterInput!]
  capabilityRules: CapabilityRuleFilterInput
  configId: StringFilterInput
  dagNodeSemanticVersion: StringFilterInput
  dataflowEndpointDetails: StringFilterInput
  dataflowSourceConfigId: StringFilterInput
  dataflowUuid: StringFilterInput
  executionContextId: StringFilterInput
  inputDataflowType: DataflowTypeFilterInput
  kmsDataKey: StringFilterInput
  kmsKeyId: StringFilterInput
  matchedCapabilityArns: StringFilterInput
  nodeConfigCapabilityType: ConfigCapabilityTypeFilterInput
  outputDataflowType: DataflowTypeFilterInput
  outputTypeToNextConfigIdsMap: StringToStringMapFilterInput
  outputTypeToPreviousConfigIdsMap: StringToStringMapFilterInput
  region: StringFilterInput
  reservationId: StringFilterInput
  reservationStatus: ReservationStatusFilterInput
  reservedCapabilityArn: StringFilterInput
  s3RecordingDetails: StringFilterInput
  sameReservationGroupAsConfigIds: StringFilterInput
  selectedLaminarFlowVersion: StringFilterInput
  supportedLaminarFlowVersions: StringFilterInput
  vita49StreamId: IntegerFilterInput
}

type DataflowEdge {
  fromConfig: Config!
  fromConfigArn: String!
  fromConfigId: String!
  toConfig: Config!
  toConfigArn: String!
  toConfigId: String!
}

input DataflowEdgeFilterInput {
  _and: [DataflowEdgeFilterInput!]
  _exists: Boolean
  _not: DataflowEdgeFilterInput
  _or: [DataflowEdgeFilterInput!]
  fromConfig: ConfigFilterInput
  fromConfigArn: StringFilterInput
  fromConfigId: StringFilterInput
  toConfig: ConfigFilterInput
  toConfigArn: StringFilterInput
  toConfigId: StringFilterInput
}

type DataflowEndpointConfigData {
  name: String!
  region: String
}

input DataflowEndpointConfigDataFilterInput {
  _and: [DataflowEndpointConfigDataFilterInput!]
  _exists: Boolean
  _not: DataflowEndpointConfigDataFilterInput
  _or: [DataflowEndpointConfigDataFilterInput!]
  name: StringFilterInput
  region: StringFilterInput
}

input DataflowEndpointConfigDataSortInput {
  name: SortOrder
  region: SortOrder
}

enum DataflowType {
  DDS
  DDS_v2
}

input DataflowTypeFilterInput {
  _eq: DataflowType
  _exists: Boolean
  _in: [DataflowType!]
  _neq: DataflowType
  _nin: [DataflowType!]
}

type DecodeConfigData {
  unvalidatedJSON: String
}

input DecodeConfigDataFilterInput {
  _and: [DecodeConfigDataFilterInput!]
  _exists: Boolean
  _not: DecodeConfigDataFilterInput
  _or: [DecodeConfigDataFilterInput!]
  unvalidatedJSON: StringFilterInput
}

input DecodeConfigDataSortInput {
  unvalidatedJSON: SortOrder
}

type DemodulationConfigData {
  unvalidatedJSON: String
}

input DemodulationConfigDataFilterInput {
  _and: [DemodulationConfigDataFilterInput!]
  _exists: Boolean
  _not: DemodulationConfigDataFilterInput
  _or: [DemodulationConfigDataFilterInput!]
  unvalidatedJSON: StringFilterInput
}

input DemodulationConfigDataSortInput {
  unvalidatedJSON: SortOrder
}

enum EphemerisSourceType {
  AZ_EL
  OEM
  SERVICE_MANAGED
  TLE
}

input EphemerisSourceTypeFilterInput {
  _eq: EphemerisSourceType
  _exists: Boolean
  _in: [EphemerisSourceType!]
  _neq: EphemerisSourceType
  _nin: [EphemerisSourceType!]
}

input FloatFilterInput {
  _eq: Float
  _exists: Boolean
  _gt: Float
  _gte: Float
  _in: [Float!]
  _lt: Float
  _lte: Float
  _neq: Float
  _nin: [Float!]
}

type Frequency {
  units: String!
  value: Float!
}

input FrequencyFilterInput {
  _and: [FrequencyFilterInput!]
  _exists: Boolean
  _not: FrequencyFilterInput
  _or: [FrequencyFilterInput!]
  units: StringFilterInput
  value: FloatFilterInput
}

input FrequencySortInput {
  units: SortOrder
  value: SortOrder
}

input GetConfigInput {
  id: String
}

input GetContactInput {
  id: String
}

input GetContactVersionInput {
  id: String
}

input GetMissionProfileInput {
  id: String
}

input IntegerFilterInput {
  _eq: Int
  _exists: Boolean
  _gt: Int
  _gte: Int
  _in: [Int!]
  _lt: Int
  _lte: Int
  _neq: Int
  _nin: [Int!]
}

type KinesisDataStreamData {
  arn: String!
  roleArn: String!
}

input KinesisDataStreamDataFilterInput {
  _and: [KinesisDataStreamDataFilterInput!]
  _exists: Boolean
  _not: KinesisDataStreamDataFilterInput
  _or: [KinesisDataStreamDataFilterInput!]
  arn: StringFilterInput
  roleArn: StringFilterInput
}

input KinesisDataStreamDataSortInput {
  arn: SortOrder
  roleArn: SortOrder
}

type LifecycleInfo {
  """Reason for cancellation"""
  cancellationReason: String

  """Cancellation timestamp"""
  cancellationTime: AWSDateTime

  """Creation timestamp"""
  createTime: AWSDateTime

  """Rescheduling timestamp"""
  reschedulingTime: AWSDateTime

  """Last update timestamp"""
  updateTime: AWSDateTime
}

input LifecycleInfoFilterInput {
  _and: [LifecycleInfoFilterInput!]
  _exists: Boolean
  _not: LifecycleInfoFilterInput
  _or: [LifecycleInfoFilterInput!]
  cancellationReason: StringFilterInput
  cancellationTime: TimestampFilterInput
  createTime: TimestampFilterInput
  reschedulingTime: TimestampFilterInput
  updateTime: TimestampFilterInput
}

input LifecycleInfoSortInput {
  cancellationReason: SortOrder
  cancellationTime: SortOrder
  createTime: SortOrder
  reschedulingTime: SortOrder
  updateTime: SortOrder
}

input ListConfigInput {
  cursor: [CursorPartInput!]
  limit: Int = 10
  sort: [ConfigSortInput!]
  where: ConfigFilterInput
}

type ListConfigOutput {
  aggregations: ConfigAggregation!
  pageInfo: PageInfo!
  results: [Config!]!
}

input ListContactInput {
  cursor: [CursorPartInput!]
  limit: Int = 10
  sort: [ContactSortInput!]
  where: ContactFilterInput
}

type ListContactOutput {
  aggregations: ContactAggregation!
  pageInfo: PageInfo!
  results: [Contact!]!
}

input ListContactVersionInput {
  cursor: [CursorPartInput!]
  limit: Int = 10
  sort: [ContactVersionSortInput!]
  where: ContactVersionFilterInput
}

type ListContactVersionOutput {
  aggregations: ContactVersionAggregation!
  pageInfo: PageInfo!
  results: [ContactVersion!]!
}

input ListMissionProfileInput {
  cursor: [CursorPartInput!]
  limit: Int = 10
  sort: [MissionProfileSortInput!]
  where: MissionProfileFilterInput
}

type ListMissionProfileOutput {
  aggregations: MissionProfileAggregation!
  pageInfo: PageInfo!
  results: [MissionProfile!]!
}

input ListReservationInput {
  limit: Int
  where: ReservationFilterInput
}

type ListReservationOutput {
  pageInfo: PageInfo!
  results: [Reservation!]!
}

type ManagedAntennaDownlinkDemodDecodeConfigData {
  managedRadioConfigId: String!
  spectrumConfig: SpectrumConfig!
  supportedOutputTypes: [SupportedOutputType!]!
}

input ManagedAntennaDownlinkDemodDecodeConfigDataFilterInput {
  _and: [ManagedAntennaDownlinkDemodDecodeConfigDataFilterInput!]
  _exists: Boolean
  _not: ManagedAntennaDownlinkDemodDecodeConfigDataFilterInput
  _or: [ManagedAntennaDownlinkDemodDecodeConfigDataFilterInput!]
  managedRadioConfigId: StringFilterInput
  spectrumConfig: SpectrumConfigFilterInput
  supportedOutputTypes: SupportedOutputTypeFilterInput
}

input ManagedAntennaDownlinkDemodDecodeConfigDataSortInput {
  managedRadioConfigId: SortOrder
  spectrumConfig: SpectrumConfigSortInput
}

type Measure {
  unit: String!
  value: Float!
}

input MeasureFilterInput {
  _and: [MeasureFilterInput!]
  _exists: Boolean
  _not: MeasureFilterInput
  _or: [MeasureFilterInput!]
  unit: StringFilterInput
  value: FloatFilterInput
}

input MeasureSortInput {
  unit: SortOrder
  value: SortOrder
}

type MeteringInfo {
  """Billable dead time in seconds"""
  billableDeadTimeSeconds: Int

  """Metering archive ID"""
  meteringArchiveId: String

  """Metering status for billing"""
  meteringStatus: MeteringStatus

  """Metering usage type"""
  meteringUsage: MeteringUsage

  """AWS payer token"""
  payerToken: String
}

type MeteringInfoAggregation {
  billableDeadTimeSeconds: Aggregation!
}

input MeteringInfoFilterInput {
  _and: [MeteringInfoFilterInput!]
  _exists: Boolean
  _not: MeteringInfoFilterInput
  _or: [MeteringInfoFilterInput!]
  billableDeadTimeSeconds: IntegerFilterInput
  meteringArchiveId: StringFilterInput
  meteringStatus: MeteringStatusFilterInput
  meteringUsage: MeteringUsageFilterInput
  payerToken: StringFilterInput
}

input MeteringInfoSortInput {
  billableDeadTimeSeconds: SortOrder
  meteringArchiveId: SortOrder
  meteringStatus: SortOrder
  meteringUsage: SortOrder
  payerToken: SortOrder
}

enum MeteringStatus {
  CHECK_FOR_DUPLICATE
  CHECK_FOR_DUPLICATE_STOPPED
  METERING_FAILED
  METERING_QUEUED
  METERING_REQUIRED_CANCELLED
  METERING_REQUIRED_COMPLETED
  METERING_REQUIRED_DIGITAL_TWIN
  METERING_REQUIRED_NEGATIVE
}

input MeteringStatusFilterInput {
  _eq: MeteringStatus
  _exists: Boolean
  _in: [MeteringStatus!]
  _neq: MeteringStatus
  _nin: [MeteringStatus!]
}

enum MeteringUsage {
  MIDBAND
  NARROWBAND
  WIDEBAND
}

input MeteringUsageFilterInput {
  _eq: MeteringUsage
  _exists: Boolean
  _in: [MeteringUsage!]
  _neq: MeteringUsage
  _nin: [MeteringUsage!]
}

type MissionProfile {
  awsAccountId: String!
  contactPostPassDurationSeconds: Int!
  contactPrePassDurationSeconds: Int!
  dataflowEdges: [DataflowEdge!]!
  expirationTime: AWSDateTime
  id: String!
  kmsDataKeyGenerationKeyGrantId: String
  kmsDecryptKeyGrantId: String
  kmsKeyGrantId: String
  minViableContactDurationSeconds: Int!
  name: String
  region: String!
  streamsKmsKey: StreamsKmsKeyInfo
  streamsKmsRole: String
  telemetrySinkConfig: Config
  telemetrySinkConfigArn: String
  telemetrySinkConfigId: String
  timestamp: AWSDateTime
  trackingConfig: Config
  trackingConfigArn: String
  trackingConfigId: String
  version: Int
}

type MissionProfileAggregation {
  contactPostPassDurationSeconds: Aggregation!
  contactPrePassDurationSeconds: Aggregation!
  minViableContactDurationSeconds: Aggregation!
  telemetrySinkConfig: ConfigAggregation!
  trackingConfig: ConfigAggregation!
  version: Aggregation!
}

input MissionProfileFilterInput {
  _and: [MissionProfileFilterInput!]
  _exists: Boolean
  _not: MissionProfileFilterInput
  _or: [MissionProfileFilterInput!]
  awsAccountId: StringFilterInput
  contactPostPassDurationSeconds: IntegerFilterInput
  contactPrePassDurationSeconds: IntegerFilterInput
  dataflowEdges: DataflowEdgeFilterInput
  expirationTime: TimestampFilterInput
  id: StringFilterInput
  kmsDataKeyGenerationKeyGrantId: StringFilterInput
  kmsDecryptKeyGrantId: StringFilterInput
  kmsKeyGrantId: StringFilterInput
  minViableContactDurationSeconds: IntegerFilterInput
  name: StringFilterInput
  region: StringFilterInput
  streamsKmsKey: StreamsKmsKeyInfoFilterInput
  streamsKmsRole: StringFilterInput
  telemetrySinkConfig: ConfigFilterInput
  telemetrySinkConfigArn: StringFilterInput
  telemetrySinkConfigId: StringFilterInput
  timestamp: TimestampFilterInput
  trackingConfig: ConfigFilterInput
  trackingConfigArn: StringFilterInput
  trackingConfigId: StringFilterInput
  version: IntegerFilterInput
}

input MissionProfileSortInput {
  awsAccountId: SortOrder
  contactPostPassDurationSeconds: SortOrder
  contactPrePassDurationSeconds: SortOrder
  expirationTime: SortOrder
  id: SortOrder
  kmsDataKeyGenerationKeyGrantId: SortOrder
  kmsDecryptKeyGrantId: SortOrder
  kmsKeyGrantId: SortOrder
  minViableContactDurationSeconds: SortOrder
  name: SortOrder
  region: SortOrder
  streamsKmsKey: StreamsKmsKeyInfoSortInput
  streamsKmsRole: SortOrder
  telemetrySinkConfig: ConfigSortInput
  telemetrySinkConfigArn: SortOrder
  telemetrySinkConfigId: SortOrder
  timestamp: SortOrder
  trackingConfig: ConfigSortInput
  trackingConfigArn: SortOrder
  trackingConfigId: SortOrder
  version: SortOrder
}

type OemProgramTrackSettings {
  ephemerisId: String!
}

type OemProgramTrackSettingsData {
  """Ephemeris ID for OEM tracking"""
  ephemerisId: String!
}

input OemProgramTrackSettingsDataFilterInput {
  _and: [OemProgramTrackSettingsDataFilterInput!]
  _exists: Boolean
  _not: OemProgramTrackSettingsDataFilterInput
  _or: [OemProgramTrackSettingsDataFilterInput!]
  ephemerisId: StringFilterInput
}

input OemProgramTrackSettingsDataSortInput {
  ephemerisId: SortOrder
}

input OemProgramTrackSettingsFilterInput {
  _and: [OemProgramTrackSettingsFilterInput!]
  _exists: Boolean
  _not: OemProgramTrackSettingsFilterInput
  _or: [OemProgramTrackSettingsFilterInput!]
  ephemerisId: StringFilterInput
}

input OemProgramTrackSettingsSortInput {
  ephemerisId: SortOrder
}

type PageInfo {
  hasNextPage: Boolean!
  lastCursor: [CursorPart!]
  total: Total!
}

enum Polarization {
  BOTH
  LEFT_HAND
  NONE
  RIGHT_HAND
}

input PolarizationFilterInput {
  _eq: Polarization
  _exists: Boolean
  _in: [Polarization!]
  _neq: Polarization
  _nin: [Polarization!]
}

type ProgramTrackSettings {
  azEl: AzElProgramTrackSettings
  oem: OemProgramTrackSettings
  tle: TleProgramTrackSettings
}

type ProgramTrackSettingsData {
  """Azimuth-Elevation program track settings"""
  azElProgramTrackSettingsData: AzElProgramTrackSettingsData

  """OEM program track settings"""
  oemProgramTrackSettingsData: OemProgramTrackSettingsData

  """TLE program track settings"""
  tleProgramTrackSettingsData: TleProgramTrackSettingsData
}

input ProgramTrackSettingsDataFilterInput {
  _and: [ProgramTrackSettingsDataFilterInput!]
  _exists: Boolean
  _not: ProgramTrackSettingsDataFilterInput
  _or: [ProgramTrackSettingsDataFilterInput!]
  azElProgramTrackSettingsData: AzElProgramTrackSettingsDataFilterInput
  oemProgramTrackSettingsData: OemProgramTrackSettingsDataFilterInput
  tleProgramTrackSettingsData: TleProgramTrackSettingsDataFilterInput
}

input ProgramTrackSettingsDataSortInput {
  azElProgramTrackSettingsData: AzElProgramTrackSettingsDataSortInput
  oemProgramTrackSettingsData: OemProgramTrackSettingsDataSortInput
  tleProgramTrackSettingsData: TleProgramTrackSettingsDataSortInput
}

input ProgramTrackSettingsFilterInput {
  _and: [ProgramTrackSettingsFilterInput!]
  _exists: Boolean
  _not: ProgramTrackSettingsFilterInput
  _or: [ProgramTrackSettingsFilterInput!]
  azEl: AzElProgramTrackSettingsFilterInput
  oem: OemProgramTrackSettingsFilterInput
  tle: TleProgramTrackSettingsFilterInput
}

input ProgramTrackSettingsSortInput {
  azEl: AzElProgramTrackSettingsSortInput
  oem: OemProgramTrackSettingsSortInput
  tle: TleProgramTrackSettingsSortInput
}

type Query {
  getConfig(cazJustification: String, input: GetConfigInput!): Config

  """Contact entity representing a scheduled satellite contact"""
  getContact(cazJustification: String, input: GetContactInput!): Contact
  getContactVersion(cazJustification: String, input: GetContactVersionInput!): ContactVersion
  getMissionProfile(cazJustification: String, input: GetMissionProfileInput!): MissionProfile
  listConfig(cazJustification: String, input: ListConfigInput!): ListConfigOutput

  """Contact entity representing a scheduled satellite contact"""
  listContact(cazJustification: String, input: ListContactInput!): ListContactOutput
  listContactVersion(cazJustification: String, input: ListContactVersionInput!): ListContactVersionOutput
  listMissionProfile(cazJustification: String, input: ListMissionProfileInput!): ListMissionProfileOutput
  listReservation(cazJustification: String, input: ListReservationInput!): ListReservationOutput
}

type RegionalInfo {
  """Region where contact was created"""
  createdInRegion: String!

  """Target region where contact occurs"""
  targetRegion: String!
}

input RegionalInfoFilterInput {
  _and: [RegionalInfoFilterInput!]
  _exists: Boolean
  _not: RegionalInfoFilterInput
  _or: [RegionalInfoFilterInput!]
  createdInRegion: StringFilterInput
  targetRegion: StringFilterInput
}

input RegionalInfoSortInput {
  createdInRegion: SortOrder
  targetRegion: SortOrder
}

enum Relation {
  eq
  gte
}

type Reservation {
  reservationGroupId: String!
  reservationType: ReservationType!
  timingInfo: ReservationTimingInfo
}

input ReservationFilterInput {
  reservationGroupId: StringFilterInput
  reservationType: ReservationTypeFilterInput
  timingInfo: ReservationTimingInfoFilterInput
}

type ReservationInfo {
  """List of capability ARNs to test"""
  capabilitiesToTestList: [String!]

  """Map of config IDs to DAG nodes"""
  configIdToDagNodeMap: [ConfigIdToDagNodeMapEntry!]

  """List of reasons if contact failed to schedule"""
  failedToScheduleReasons: [String!]

  """List of obsolete reservation ARNs"""
  obsoleteReservationArnList: [String!]

  """List of active reservation ARNs"""
  reservationArnList: [String!]

  """Reserve contact step function ID"""
  reserveContactStepFunctionId: String
}

input ReservationInfoFilterInput {
  _and: [ReservationInfoFilterInput!]
  _exists: Boolean
  _not: ReservationInfoFilterInput
  _or: [ReservationInfoFilterInput!]
  capabilitiesToTestList: StringFilterInput
  configIdToDagNodeMap: ConfigIdToDagNodeMapFilterInput
  failedToScheduleReasons: StringFilterInput
  obsoleteReservationArnList: StringFilterInput
  reservationArnList: StringFilterInput
  reserveContactStepFunctionId: StringFilterInput
}

input ReservationInfoSortInput {
  reserveContactStepFunctionId: SortOrder
}

enum ReservationStatus {
  AWS_FAILED
  CANCELLED
  COMPLETED
  DELETED
  FAILED
  PASS
  POSTPASS
  PREPASS
  SCHEDULED
  SCHEDULING
}

input ReservationStatusFilterInput {
  _eq: ReservationStatus
  _exists: Boolean
  _in: [ReservationStatus!]
  _neq: ReservationStatus
  _nin: [ReservationStatus!]
}

type ReservationTimingInfo {
  endTime: AWSDateTime
  startTime: AWSDateTime
}

input ReservationTimingInfoFilterInput {
  endTime: TimestampFilterInput
  startTime: TimestampFilterInput
}

enum ReservationType {
  CONTACT
  DEPLOYMENT_PLANNED
  DEPLOYMENT_UNPLANNED
  GROUND_STATION_SPECIFIC
  MAINTENANCE_PLANNED
  MAINTENANCE_UNPLANNED
  UNAVAILABLE_PLANNED
  UNAVAILABLE_UNPLANNED
}

input ReservationTypeFilterInput {
  _neq: ReservationType
}

type S3PlaybackConfigData {
  bandwidth: Frequency
  bucket: BucketData!
  keys: [String!]!
  roleArn: String!
}

input S3PlaybackConfigDataFilterInput {
  _and: [S3PlaybackConfigDataFilterInput!]
  _exists: Boolean
  _not: S3PlaybackConfigDataFilterInput
  _or: [S3PlaybackConfigDataFilterInput!]
  bandwidth: FrequencyFilterInput
  bucket: BucketDataFilterInput
  keys: StringFilterInput
  roleArn: StringFilterInput
}

input S3PlaybackConfigDataSortInput {
  bandwidth: FrequencySortInput
  bucket: BucketDataSortInput
  roleArn: SortOrder
}

type S3RecordingConfigData {
  bucket: BucketData!
  prefix: String
  roleArn: String!
}

input S3RecordingConfigDataFilterInput {
  _and: [S3RecordingConfigDataFilterInput!]
  _exists: Boolean
  _not: S3RecordingConfigDataFilterInput
  _or: [S3RecordingConfigDataFilterInput!]
  bucket: BucketDataFilterInput
  prefix: StringFilterInput
  roleArn: StringFilterInput
}

input S3RecordingConfigDataSortInput {
  bucket: BucketDataSortInput
  prefix: SortOrder
  roleArn: SortOrder
}

type SigMaps {
  """List of signature maps for regional reservations"""
  createReservationForRegionSignatureMaps: [SignatureMapData!]

  """Signature map for authorization"""
  signatureMap: SignatureMapData
}

input SigMapsFilterInput {
  _and: [SigMapsFilterInput!]
  _exists: Boolean
  _not: SigMapsFilterInput
  _or: [SigMapsFilterInput!]
  createReservationForRegionSignatureMaps: SignatureMapDataFilterInput
  signatureMap: SignatureMapDataFilterInput
}

input SigMapsSortInput {
  signatureMap: SignatureMapDataSortInput
}

type SignatureBooleanMapEntry {
  key: String
  value: Boolean
}

input SignatureBooleanMapFilterInput {
  _and: [SignatureBooleanMapFilterInput!]
  _exists: Boolean
  _not: SignatureBooleanMapFilterInput
  _or: [SignatureBooleanMapFilterInput!]
  key: StringFilterInput
  value: BooleanFilterInput
}

type SignatureMapData {
  """List of aggregate signatures"""
  aggregates: [String!]

  """Namespace for the signature map"""
  namespace: String

  """Map of signature keys to boolean values"""
  signatureMap: [SignatureBooleanMapEntry!]

  """Type identifier"""
  typeId: String
}

input SignatureMapDataFilterInput {
  _and: [SignatureMapDataFilterInput!]
  _exists: Boolean
  _not: SignatureMapDataFilterInput
  _or: [SignatureMapDataFilterInput!]
  aggregates: StringFilterInput
  namespace: StringFilterInput
  signatureMap: SignatureBooleanMapFilterInput
  typeId: StringFilterInput
}

input SignatureMapDataSortInput {
  namespace: SortOrder
  typeId: SortOrder
}

enum SortOrder {
  asc
  desc
}

type SpectrumConfig {
  bandwidth: Frequency!
  centerFrequency: Frequency!
  polarization: Polarization!
}

input SpectrumConfigFilterInput {
  _and: [SpectrumConfigFilterInput!]
  _exists: Boolean
  _not: SpectrumConfigFilterInput
  _or: [SpectrumConfigFilterInput!]
  bandwidth: FrequencyFilterInput
  centerFrequency: FrequencyFilterInput
  polarization: PolarizationFilterInput
}

input SpectrumConfigSortInput {
  bandwidth: FrequencySortInput
  centerFrequency: FrequencySortInput
  polarization: SortOrder
}

type StreamsKmsKeyInfo {
  keyArnUsedInGrant: String
  kmsAliasArn: String
  kmsAliasName: String
  kmsKeyArn: String
}

input StreamsKmsKeyInfoFilterInput {
  _and: [StreamsKmsKeyInfoFilterInput!]
  _exists: Boolean
  _not: StreamsKmsKeyInfoFilterInput
  _or: [StreamsKmsKeyInfoFilterInput!]
  keyArnUsedInGrant: StringFilterInput
  kmsAliasArn: StringFilterInput
  kmsAliasName: StringFilterInput
  kmsKeyArn: StringFilterInput
}

input StreamsKmsKeyInfoSortInput {
  keyArnUsedInGrant: SortOrder
  kmsAliasArn: SortOrder
  kmsAliasName: SortOrder
  kmsKeyArn: SortOrder
}

input StringFilterInput {
  _eq: String
  _exists: Boolean
  _gt: String
  _gte: String
  _in: [String!]
  _lt: String
  _lte: String
  _neq: String
  _nin: [String!]
  _startsWith: String
}

type StringToStringMapEntry {
  key: String
  value: String
}

input StringToStringMapFilterInput {
  _and: [StringToStringMapFilterInput!]
  _exists: Boolean
  _not: StringToStringMapFilterInput
  _or: [StringToStringMapFilterInput!]
  key: StringFilterInput
  value: StringFilterInput
}

type SupportedOutputType {
  description: String!
  name: String!
}

input SupportedOutputTypeFilterInput {
  _and: [SupportedOutputTypeFilterInput!]
  _exists: Boolean
  _not: SupportedOutputTypeFilterInput
  _or: [SupportedOutputTypeFilterInput!]
  description: StringFilterInput
  name: StringFilterInput
}

type TelemetrySinkData {
  kinesisDataStream: KinesisDataStreamData!
}

input TelemetrySinkDataFilterInput {
  _and: [TelemetrySinkDataFilterInput!]
  _exists: Boolean
  _not: TelemetrySinkDataFilterInput
  _or: [TelemetrySinkDataFilterInput!]
  kinesisDataStream: KinesisDataStreamDataFilterInput
}

input TelemetrySinkDataSortInput {
  kinesisDataStream: KinesisDataStreamDataSortInput
}

input TimestampFilterInput {
  _eq: AWSDateTime
  _exists: Boolean
  _gt: AWSDateTime
  _gte: AWSDateTime
  _in: [AWSDateTime!]
  _lt: AWSDateTime
  _lte: AWSDateTime
  _neq: AWSDateTime
  _nin: [AWSDateTime!]
}

type TimingInfo {
  """Contact timeline status"""
  contactTimeline: ContactTimeline

  """End time of the contact in epoch seconds (excludes postpass)"""
  endTime: AWSDateTime!

  """Post-pass end time in epoch seconds"""
  postPassEndTime: AWSDateTime

  """Pre-pass start time in epoch seconds"""
  prePassStartTime: AWSDateTime

  """Start time of the contact in epoch seconds (excludes prepass)"""
  startTime: AWSDateTime!

  """Stop time if contact was stopped early"""
  stopTime: AWSDateTime

  """Visibility end time in epoch seconds"""
  visibilityEndTime: AWSDateTime

  """Visibility start time in epoch seconds"""
  visibilityStartTime: AWSDateTime
}

input TimingInfoFilterInput {
  _and: [TimingInfoFilterInput!]
  _exists: Boolean
  _not: TimingInfoFilterInput
  _or: [TimingInfoFilterInput!]
  contactTimeline: ContactTimelineFilterInput
  endTime: TimestampFilterInput
  postPassEndTime: TimestampFilterInput
  prePassStartTime: TimestampFilterInput
  startTime: TimestampFilterInput
  stopTime: TimestampFilterInput
  visibilityEndTime: TimestampFilterInput
  visibilityStartTime: TimestampFilterInput
}

input TimingInfoSortInput {
  contactTimeline: SortOrder
  endTime: SortOrder
  postPassEndTime: SortOrder
  prePassStartTime: SortOrder
  startTime: SortOrder
  stopTime: SortOrder
  visibilityEndTime: SortOrder
  visibilityStartTime: SortOrder
}

type TleProgramTrackSettings {
  ephemerisId: String!
}

type TleProgramTrackSettingsData {
  """Ephemeris ID for TLE tracking"""
  ephemerisId: String!
}

input TleProgramTrackSettingsDataFilterInput {
  _and: [TleProgramTrackSettingsDataFilterInput!]
  _exists: Boolean
  _not: TleProgramTrackSettingsDataFilterInput
  _or: [TleProgramTrackSettingsDataFilterInput!]
  ephemerisId: StringFilterInput
}

input TleProgramTrackSettingsDataSortInput {
  ephemerisId: SortOrder
}

input TleProgramTrackSettingsFilterInput {
  _and: [TleProgramTrackSettingsFilterInput!]
  _exists: Boolean
  _not: TleProgramTrackSettingsFilterInput
  _or: [TleProgramTrackSettingsFilterInput!]
  ephemerisId: StringFilterInput
}

input TleProgramTrackSettingsSortInput {
  ephemerisId: SortOrder
}

type Total {
  relation: Relation
  value: Int
}

type TrackingConfigData {
  autotrackCriticality: Criticality!
}

input TrackingConfigDataFilterInput {
  _and: [TrackingConfigDataFilterInput!]
  _exists: Boolean
  _not: TrackingConfigDataFilterInput
  _or: [TrackingConfigDataFilterInput!]
  autotrackCriticality: CriticalityFilterInput
}

input TrackingConfigDataSortInput {
  autotrackCriticality: SortOrder
}

type TrackingInfo {
  """List of ephemeris information"""
  ephemerisInfoList: [ContactEphemerisInfo!]

  """Input tracking overrides data"""
  inputTrackingOverridesData: TrackingOverridesData

  """Maximum elevation in degrees"""
  maxElevationDegrees: Float

  """Pointing angle sequence ID"""
  pointingAngleSequenceId: String
}

type TrackingInfoAggregation {
  maxElevationDegrees: Aggregation!
}

input TrackingInfoFilterInput {
  _and: [TrackingInfoFilterInput!]
  _exists: Boolean
  _not: TrackingInfoFilterInput
  _or: [TrackingInfoFilterInput!]
  ephemerisInfoList: ContactEphemerisInfoFilterInput
  inputTrackingOverridesData: TrackingOverridesDataFilterInput
  maxElevationDegrees: FloatFilterInput
  pointingAngleSequenceId: StringFilterInput
}

input TrackingInfoSortInput {
  inputTrackingOverridesData: TrackingOverridesDataSortInput
  maxElevationDegrees: SortOrder
  pointingAngleSequenceId: SortOrder
}

type TrackingOverrides {
  autoTrackSettings: AutoTrackSettings
  programTrackSettings: ProgramTrackSettings
}

type TrackingOverridesData {
  """Auto track settings data"""
  autoTrackSettingsData: AutoTrackSettingsData

  """Program track settings data"""
  programTrackSettingsData: ProgramTrackSettingsData
}

input TrackingOverridesDataFilterInput {
  _and: [TrackingOverridesDataFilterInput!]
  _exists: Boolean
  _not: TrackingOverridesDataFilterInput
  _or: [TrackingOverridesDataFilterInput!]
  autoTrackSettingsData: AutoTrackSettingsDataFilterInput
  programTrackSettingsData: ProgramTrackSettingsDataFilterInput
}

input TrackingOverridesDataSortInput {
  autoTrackSettingsData: AutoTrackSettingsDataSortInput
  programTrackSettingsData: ProgramTrackSettingsDataSortInput
}

input TrackingOverridesFilterInput {
  _and: [TrackingOverridesFilterInput!]
  _exists: Boolean
  _not: TrackingOverridesFilterInput
  _or: [TrackingOverridesFilterInput!]
  autoTrackSettings: AutoTrackSettingsFilterInput
  programTrackSettings: ProgramTrackSettingsFilterInput
}

input TrackingOverridesSortInput {
  autoTrackSettings: AutoTrackSettingsSortInput
  programTrackSettings: ProgramTrackSettingsSortInput
}

type UplinkEchoConfigData {
  antennaUplinkArn: String!
  centerFrequency: Frequency!
  enabled: Boolean!
  polarity: Polarization!
}

input UplinkEchoConfigDataFilterInput {
  _and: [UplinkEchoConfigDataFilterInput!]
  _exists: Boolean
  _not: UplinkEchoConfigDataFilterInput
  _or: [UplinkEchoConfigDataFilterInput!]
  antennaUplinkArn: StringFilterInput
  centerFrequency: FrequencyFilterInput
  enabled: BooleanFilterInput
  polarity: PolarizationFilterInput
}

input UplinkEchoConfigDataSortInput {
  antennaUplinkArn: SortOrder
  centerFrequency: FrequencySortInput
  enabled: SortOrder
  polarity: SortOrder
}
`;
